import { FilterList } from "@mui/icons-material";
import {
  Grid,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import IconButton from '@mui/material/IconButton';
import BigCalendar from "components/Calender/big-calender";
import { useAlert } from "contexts/alertContext";
import { getWorkshops } from "framework/workshops";
import { getUserData } from "helpers";
import { handleError } from "helpers/utils";
import useApp from "hooks/useApp";
import { Feature } from "models/api";
import { IWorkshop } from "models/workshop";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryClient } from "react-query";
import "rsuite/Calendar/styles/index.css";
import { UserRoleName } from "types/auth";
import CalendarFilterModal from "./partials/CalendarFilterModal";

interface Props {
  feature: Feature;
}
const queryClient = new QueryClient();

const CalendarTraining: React.FC<Props> = ({ feature }) => {
  const { t } = useTranslation();
  const { push } = useApp();
  const user = getUserData();
  const { colors, roleName } = user;

  const [filters, setFilters] = useState<any>(null);
  const [workshops, setWorkshops] = useState<IWorkshop[]>([]);
  const [filteredWorkshops, setFilteredWorkshops] = useState<IWorkshop[]>([]);
  const [filterState, setFilterState] = useState({
    department: "",
    provider: "",
    vendors: "",
    venues: "",
    topic: "",
    modalities: "",
    countries: "",
    cities: "",
    languages: "",
  });
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState<Date>(new Date());
  const [openFilter, setOpenFilter] = useState(false);
  const { setAlert } = useAlert();

  const handleFilterChange = (key: string, value: string) => {
    setFilterState((prev) => ({ ...prev, [key]: value }));
    makeRequest({ ...filterState, [key]: value });
  };

  const resetFilters = () => {
    const initialFilterState = {
      department: "",
      provider: "",
      vendors: "",
      venues: "",
      topic: "",
      modalities: "",
      countries: "",
      cities: "",
      languages: "",
    };
    setFilterState(initialFilterState);
    makeRequest(initialFilterState);
  };

  const makeRequest = (filterData: typeof filterState) => {
    let filtered = workshops;
    const { department, vendors, venues, provider, topic, modalities, countries, cities, languages } = filterData;

    if (provider) filtered = filtered.filter((w) => w.companyId === provider);
    if (topic) filtered = filtered.filter((w) => w.topicId === topic);
    if (modalities) filtered = filtered.filter((w) => w.modality === +modalities);
    if (venues) filtered = filtered.filter((w) => w.venueId === venues);
    if (vendors) filtered = filtered.filter((w) => w.vendorId === vendors);
    if (countries) filtered = filtered.filter((w) => w.countryId === countries);
    if (cities) filtered = filtered.filter((w) => w.cityId === cities);
    if (languages) filtered = filtered.filter((w) => w.languages.find((l) => l.id === languages));

    setFilteredWorkshops(filtered);
  };

  const fetchWorkshops = useCallback(async () => {
    const month = date.getMonth() + 1; // Months are zero-based
    const year = date.getFullYear();

    try {
      const response = await queryClient.fetchQuery("getWorkshops", () => getWorkshops(`/${month}/${year}`));
      setWorkshops(response);
      setFilteredWorkshops(response);
      setLoading(false);
    } catch (error) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }
  }, [date, setAlert]);

  const fetchFilters = useCallback(async () => {
    try {
      const response = await queryClient.fetchQuery("getWorkshopFilters", () => getWorkshops(`/filters`));
      setFilters(response);
      setLoading(false);
    } catch (error) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }
  }, [setAlert]);

  useEffect(() => {
    fetchWorkshops();
    fetchFilters();
  }, [date]);

  if (loading) {
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );
  }

  const events = filteredWorkshops.map((workshop) => ({
    start: moment(workshop.fromDate).toDate(),
    end: moment(workshop.toDate).add(1, 'day').toDate(),
    resource: { id: workshop.workshopId },
    title: `${workshop.topicName} ${workshop.cityName ? ` - ${workshop.cityName}` : ""}
    ${workshop.venueName ? ` - ${workshop.venueName}` : ""}`,
  }));

  const handleSelectedEvent = (event: any) => {
    push(`/workshops/${event.resource.id}`);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
          <Stack direction="row" spacing={1} justifyContent="space-between" width="100%">
            <Typography variant="h3" color={colors?.labelColor || "header.primary"}>
              {t(feature.name)}
            </Typography>
            {roleName !== UserRoleName.EMPLOYEE && (
              <Tooltip title={t('T_filter_calender')}>
                <IconButton onClick={() => setOpenFilter(true)}>
                  <FilterList />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        <div style={{ height: "100vh" }}>
          <BigCalendar
            events={events}
            defaultDate={date}
            onSelectEvent={handleSelectedEvent}
            popup
            onNavigate={setDate}
          />
        </div>
      </Stack>

      {openFilter && (
        <CalendarFilterModal
          open={openFilter}
          filters={filters}
          {...filterState}
          onClose={() => setOpenFilter(false)}
          onReset={resetFilters}
          onChangeCities={(e: SelectChangeEvent<string>) => handleFilterChange("cities", e.target.value)}
          onChangeCountries={(e: SelectChangeEvent<string>) => handleFilterChange("countries", e.target.value)}
          onChangeLanguages={(e: SelectChangeEvent<string>) => handleFilterChange("languages", e.target.value)}
          onChangeModalities={(e: SelectChangeEvent<string>) => handleFilterChange("modalities", e.target.value)}
          onChangeTopic={(e: SelectChangeEvent<string>) => handleFilterChange("topic", e.target.value)}
          onChangeVenues={(e: SelectChangeEvent<string>) => handleFilterChange("venues", e.target.value)}
          onChangeVendor={(e: SelectChangeEvent<string>) => handleFilterChange("vendors", e.target.value)}
        />
      )}
    </>
  );
};

export default CalendarTraining;
